import { Hash } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { HelpLabel } from '@/features/guided-help';
import type { TournamentConfigFieldConfig } from './types';
import { DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG } from './types';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

export interface MinSchedulesRequiredFieldProps {
  value: number;
  onChange: (value: number) => void;
  config?: TournamentConfigFieldConfig;
  wrapInCard?: boolean;
  inputId?: string;
}

export function MinSchedulesRequiredField({
  value,
  onChange,
  config: userConfig,
  wrapInCard = true,
  inputId = 'min-schedules-required',
}: MinSchedulesRequiredFieldProps) {
  const config = { ...DEFAULT_TOURNAMENT_CONFIG_FIELD_CONFIG, ...userConfig };

  const min = config.minSchedulesRequired?.min ?? 1;
  const max = config.minSchedulesRequired?.max ?? 50;

  const handleChange = (next: number) => {
    const clamped = Math.max(min, Math.min(Math.floor(next), max));
    if (!Number.isNaN(clamped)) {
      onChange(clamped);
    }
  };

  const field = (
    <div className="space-y-3" data-guide-id="wizard.config.minSchedulesRequired">
      <HelpLabel htmlFor={inputId} topicId="wizard.config.minSchedulesRequired">
        Mínimo de horarios que debe elegir el jugador *
      </HelpLabel>
      <div className="flex items-center gap-4">
        <Input
          id={inputId}
          type="number"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={(e) =>
            handleChange(parseInt(e.target.value, 10))
          }
          className="w-24 text-center text-lg font-medium"
        />
        <span className="text-gray-500">
          {value === 1 ? 'horario' : 'horarios'}
        </span>
      </div>
      <p className="text-sm text-gray-500">
        Cantidad mínima de turnos individuales que el jugador debe marcar como
        disponibles al inscribirse. Mínimo: {min}.
      </p>
    </div>
  );

  if (!wrapInCard) {
    return <div className={wizardBlockShellClassName}>{field}</div>;
  }

  return (
    <Card className={wizardBlockCardClassName}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Hash className="h-5 w-5 text-blue-600" />
          Disponibilidad mínima
        </CardTitle>
      </CardHeader>
      <CardContent>{field}</CardContent>
    </Card>
  );
}
